import React, { useMemo } from 'react';
import { OrderBreakdown } from '../../../types/statistics';

interface OrderBreakdownListProps {
  orderBreakdowns: OrderBreakdown[];
}

export const OrderBreakdownList: React.FC<OrderBreakdownListProps> = ({ orderBreakdowns }) => {
  const groupedOrders = useMemo(() => {
    // Group by customer
    const groups: Record<string, OrderBreakdown[]> = {};
    orderBreakdowns.forEach(order => {
      const customer = order.customerName || 'Sin Cliente';
      if (!groups[customer]) {
        groups[customer] = [];
      }
      groups[customer].push(order);
    });
    
    // Sort keys alphabetically and orders by date, and sort items alphabetically within each order
    return Object.keys(groups).sort().reduce((acc, key) => {
      const sortedOrders = groups[key].sort((a, b) => new Date(a.orderDate).getTime() - new Date(b.orderDate).getTime());
      
      // Sort items within each order
      sortedOrders.forEach(order => {
        order.items.sort((a, b) => a.productName.localeCompare(b.productName));
      });

      acc[key] = sortedOrders;
      return acc;
    }, {} as Record<string, OrderBreakdown[]>);
  }, [orderBreakdowns]);

  if (orderBreakdowns.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
        <p className="text-gray-500">No hay ventas en este período</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden print:border-none print:shadow-none">
      <div className="px-6 py-4 border-b border-gray-200 print:hidden">
        <h3 className="text-lg font-semibold text-gray-900">Desglose por Venta</h3>
      </div>
      
      <div className="space-y-4 print:space-y-2 bg-white p-6 print:p-0">
        {Object.entries(groupedOrders).map(([customerName, orders]) => (
          <div key={customerName} className="break-inside-avoid-page">
            {/* Customer Header - break-after: avoid ensures it sticks to the table */}
            <div className="bg-gray-100 px-6 py-3 text-sm font-bold text-gray-900 border border-gray-200 rounded-t-lg print:border-slate-300 print:bg-slate-50 break-after-avoid print:px-2 print:py-1">
              {customerName}
            </div>

            {/* Table */}
            <div className="border border-t-0 border-gray-200 rounded-b-lg overflow-hidden print:border-slate-300">
              <table className="w-full table-fixed">
                <thead className="bg-gray-50 border-b border-gray-200 print:bg-slate-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-40 print:px-2 print:py-1">
                      Fecha
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-auto print:px-2 print:py-1">
                      Producto
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-32 print:px-2 print:py-1">
                      Cantidad
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32 print:px-2 print:py-1">
                      Unidad
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {orders.map((order) => {
                    const orderDate = new Date(order.orderDate);
                    const formattedDate = orderDate.toLocaleDateString('es-ES', {
                      day: 'numeric',
                      month: 'long',
                    });
                    const dateDisplay = formattedDate;

                    const totalBoxes = order.items
                      .filter(i => i.unit.toLowerCase().includes('caja'))
                      .reduce((sum, i) => sum + i.quantity, 0);

                    const totalOther = order.items
                      .filter(i => !i.unit.toLowerCase().includes('caja'))
                      .reduce((sum, i) => sum + i.quantity, 0);

                    const lineItemCount = order.items.length;

                    return order.items.map((item, itemIndex) => (
                      <tr key={`${order.orderId}-${itemIndex}`} className="hover:bg-gray-50 break-inside-avoid">
                        {/* Show date only for the first item of the order (merged cell) */}
                        {itemIndex === 0 ? (
                          <td 
                            className="px-6 py-3 text-sm text-gray-500 align-top border-r border-gray-50 print:px-2 print:py-1"
                            rowSpan={order.items.length}
                          >
                            <div className="font-medium text-gray-900 mb-2">{dateDisplay}</div>
                            <div className="text-xs text-gray-500 flex flex-col gap-1">
                              {totalBoxes > 0 && (
                                <span className="font-medium text-blue-700 bg-blue-50 px-2 py-0.5 rounded-md inline-block border border-blue-100 print:text-black print:border-gray-300 print:bg-transparent">
                                  {totalBoxes} Cajas
                                </span>
                              )}
                              {totalOther > 0 && (
                                <span className="font-medium text-amber-700 bg-amber-50 px-2 py-0.5 rounded-md inline-block border border-amber-100 print:text-black print:border-gray-300 print:bg-transparent">
                                  {totalOther} Unidades
                                </span>
                              )}
                              <span className="font-medium text-slate-600 bg-slate-50 px-2 py-0.5 rounded-md inline-block border border-slate-100 print:text-black print:border-gray-300 print:bg-transparent">
                                {lineItemCount} Refs.
                              </span>
                            </div>
                          </td>
                        ) : null}
                        
                        <td className="px-6 py-3 text-sm text-gray-900 align-top break-words print:px-2 print:py-1">
                          {item.productName}
                        </td>
                        <td className="px-6 py-3 text-sm font-medium text-gray-900 text-right align-top print:px-2 print:py-1">
                          {item.quantity}
                        </td>
                        <td className="px-6 py-3 text-sm text-gray-500 capitalize align-top print:px-2 print:py-1">
                          {item.unit}
                        </td>
                      </tr>
                    ));
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
